import React, { useState, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { CloseIcon } from "@icarius-icons";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import PaperDraggable from "@icarius-common/paperDraggable";
import DialogTransition from "@icarius-common/dialogTransition";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import Loader from "@icarius-common/loader";
import useHandleForm from "./useHandleForm";
import CommonPage from "@icarius-common/commonPage";
import ResultRenderer from "@icarius-pages/collaboratorSkillsRanking/components/gridRenderers/resultRenderer";
import GapRenderer from "@icarius-pages/collaboratorSkillsRanking/components/gridRenderers/gapRenderer";

const gridStyle = { padding: 10 };

const CreateTrainingFormDialog = (props) => {

  const {
    open,
    courseList,
    skillList,
    collaboratorsList,
    isSkillBased,
    submitCallback,
    handleClose,
    isLoading,
  } = props;

  const [selectedSkill, setSelectedSkill] = useState(null);

  const handleCloseAndReset = () => {
    setSelectedSkill(null);
    handleClose();
  };

  const filteredCourseList = useMemo(() => {
    if (isSkillBased) {
      if (!selectedSkill) return [];
      return courseList.filter(course => course.skills && course.skills.includes(selectedSkill?.key));
    }
    return courseList;
  }, [courseList, isSkillBased, selectedSkill]);

  const filteredSkillList = useMemo(() => {
    return skillList.filter(skill => {
      return courseList.some(course => course.skills && course.skills.includes(skill.key));
    });
  }, [skillList, courseList]);

  const dispatch = useDispatch();
  const ownGridRef = useRef();

  const openValidationError = () => {
    dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
  }

  const {
    formData,
    setFormValue,
    submit,
  } = useHandleForm(submitCallback, openValidationError);

  const handleBeforeSubmit = () => {
    let selectedRows = [];
    if (ownGridRef.current && ownGridRef.current.api) {
      selectedRows = ownGridRef.current.api.getSelectedRows();
    }

    const collaboratorCodes = selectedRows.map((row) => row["CODIGO DE EMPLEADO"]);
    if (collaboratorCodes.length === 0) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("selectAtleastOneCollaborator"), severity: "error", duration: 10000 }));
      return;
    }

    submit({ collaborators: collaboratorCodes });
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={DialogTransition}
      PaperComponent={PaperDraggable}
      maxWidth={"md"}
      fullWidth={true}
    >
      <div className={"dialog-container"}>
        <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
          {getLocalizedString(isSkillBased ? "createSkillBasedTraining" : "createTraining")}
          <DialogTitleDivider />
        </DialogTitle>
        <CloseIcon className={"dialog-close-icon icon"} onClick={handleCloseAndReset} />
        <DialogContent>
          <Loader open={isLoading} />
          <Grid container item xs={12}>
            {isSkillBased && (
              <Grid container item alignItems="center" xs={12} style={gridStyle}>
                <FormControl style={{ width: "100%" }}>
                  <InputLabel required id={`label-skill`}>{getLocalizedString("skillToDevelop")}</InputLabel>
                  <Select
                    value={selectedSkill?.key}
                    labelId={`label-skill`}
                    id={`select-skill`}
                    onChange={(e) => {
                      setSelectedSkill(skillList.find(s => s.key === e.target.value));
                    }}
                    margin={"none"}
                  >
                    {
                      filteredSkillList.map((item) => (
                        <MenuItem
                          className={"whiteText"}
                          key={item.key}
                          value={item.key}
                        >
                          {item.value}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <FormControl style={{ width: "100%" }} disabled={isSkillBased && !selectedSkill}>
                <InputLabel required id={`label-courseId`}>{getLocalizedString("course")}</InputLabel>
                <Select
                  value={formData.courseId}
                  labelId={`label-courseId`}
                  id={`select-courseId`}
                  onChange={(e) => setFormValue(e.target.value, "courseId")}
                  margin={"none"}
                >
                  {
                    filteredCourseList.map((item) => (
                      <MenuItem
                        className={"whiteText"}
                        key={item.key}
                        value={item.key}
                      >
                        {item.value}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Grid>
            <Grid container item alignItems="center" xs={12} style={gridStyle}>
              <TextField
                required
                fullWidth
                margin={"none"}
                label={getLocalizedString("name")}
                value={formData.name}
                onChange={(e) => setFormValue(e.target.value, "name")}
              />
            </Grid>
            {isSkillBased && (<Grid container item xs={12} style={gridStyle}>
              <CommonPage
                ownGridRef={ownGridRef}
                gridTitle={getLocalizedString("collaboratorList")}
                columnDefPage={"skillBasedTraining"}
                rowData={collaboratorsList.filter(c => c["COMPETENCIA"] === selectedSkill?.value)}
                gridHeight={'40vh'}
                ignoreProcessesbyPath
                frameworkComponents={{ GapRenderer, ResultRenderer }}
              />
            </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonDialogAction onClick={handleCloseAndReset} text={getLocalizedString("cancel")} />
          <ButtonDialogAction onClick={isSkillBased ? handleBeforeSubmit : submit} isAccept text={getLocalizedString("accept")} />
        </DialogActions>
      </div>
    </Dialog>
  );
}

export default CreateTrainingFormDialog;